import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * Bundle behaviour:
 * - Add-on line items (properties._is_addon === 'true') are quantity-locked and cannot be
 *   removed individually by the customer. They only ever change as a side effect of their
 *   bundle's main item(s) being removed.
 * - Bundle "main" line items (properties._bundle_id present, not an add-on) are quantity-locked
 *   too - the customer can only delete the whole line, never bump it up/down.
 * - Deleting a bundle main line reduces every add-on that shares its _bundle_id by the quantity
 *   that was removed. If that was the last main line for the bundle, all of its add-ons are
 *   removed entirely.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    const row = this.refs.cartItemRows[line - 1];

    // Add-ons have no interactive control in the markup at all, so this should never fire for
    // them - but guard here too in case of stale DOM state or a future markup change.
    if (row && row.dataset.isAddon === 'true') return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    const bundleId = row?.dataset.bundleId;

    // Bundle "main" item: keep every add-on in the same bundle in sync with the change
    // (increase/decrease each add-on's quantity by the same amount the main item changed by).
    if (row && bundleId) {
      this.#changeBundleMainItemQuantity(line, row, bundleId, quantity);
      return;
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    // Add-on items are never individually removable by the customer - they can only
    // disappear as a side effect of removing their bundle's main item(s).
    if (cartItemRowToRemove.dataset.isAddon === 'true') return;

    const bundleId = cartItemRowToRemove.dataset.bundleId;

    if (bundleId) {
      this.#removeBundleMainItem(line, cartItemRowToRemove, bundleId);
      return;
    }

    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    this.#animateRowsRemoval([
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ]);
  }

  /**
   * Removes a bundle "main" line item and keeps its add-ons in sync.
   *
   * - If other main items remain for the same bundle, every add-on's quantity is reduced
   *   by the quantity of the line being removed (never below zero).
   * - If this was the last main item for the bundle, all add-ons belonging to that bundle
   *   are removed entirely.
   *
   * @param {number} line - The line item index (1-indexed) being removed.
   * @param {HTMLElement} row - The row element being removed.
   * @param {string} bundleId - The bundle id shared by the main item and its add-ons.
   */
  async #removeBundleMainItem(line, row, bundleId) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker('bundle-remove:user-action');

    this.#disableCartItems();

    try {
      const removedQuantity = Number(row.dataset.quantity) || 0;
      const removedKey = row.dataset.key;

      const cartResponse = await fetch('/cart.js', fetchConfig('json', { method: 'GET' }));
      const cart = await cartResponse.json();

      const remainingMainItems = cart.items.filter(
        (cartItem) =>
          cartItem.properties?._bundle_id === bundleId &&
          cartItem.properties?._is_addon !== 'true' &&
          cartItem.key !== removedKey
      );

      const addonItems = cart.items.filter(
        (cartItem) => cartItem.properties?._bundle_id === bundleId && cartItem.properties?._is_addon === 'true'
      );

      /** @type {Record<string, number>} */
      const updates = {};
      if (removedKey) updates[removedKey] = 0;

      if (remainingMainItems.length === 0) {
        // Last main item for this bundle: every add-on goes with it.
        addonItems.forEach((addon) => {
          updates[addon.key] = 0;
        });
      } else {
        // Reduce each add-on by the quantity that was just removed, floored at 0.
        addonItems.forEach((addon) => {
          updates[addon.key] = Math.max(addon.quantity - removedQuantity, 0);
        });
      }

      await this.#sendBundleUpdate(updates);
    } catch (error) {
      console.error(error);
    } finally {
      this.#enableCartItems();
      cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
    }
  }

  /**
   * Changes a bundle "main" item's quantity and keeps every add-on in the same bundle in sync:
   * each add-on's quantity is shifted by the same delta the main item just changed by
   * (increase the main item by 3 -> every add-on in the bundle goes up by 3, and vice versa).
   * Add-on quantities are floored at 0.
   *
   * @param {number} line - The line item index (1-indexed) whose quantity changed.
   * @param {HTMLElement} row - The row element for that line item.
   * @param {string} bundleId - The bundle id shared by the main item and its add-ons.
   * @param {number} newQuantity - The new (absolute) quantity for the main item.
   */
  async #changeBundleMainItemQuantity(line, row, bundleId, newQuantity) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker('change:user-action');

    this.#disableCartItems();

    try {
      const previousQuantity = Number(row.dataset.quantity) || 0;
      const delta = newQuantity - previousQuantity;

      if (delta === 0) return;

      const addonRows = this.refs.cartItemRows.filter(
        (candidateRow) => candidateRow.dataset.bundleId === bundleId && candidateRow.dataset.isAddon === 'true'
      );

      /** @type {Record<string, number>} */
      const updates = {};
      if (row.dataset.key) updates[row.dataset.key] = newQuantity;

      addonRows.forEach((addonRow) => {
        const addonKey = addonRow.dataset.key;
        if (!addonKey) return;

        const currentAddonQuantity = Number(addonRow.dataset.quantity) || 0;
        updates[addonKey] = Math.max(currentAddonQuantity + delta, 0);
      });

      await this.#sendBundleUpdate(updates, line);

      const lineItemRow = this.refs.cartItemRows[line - 1];
      const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow?.querySelector('text-component'));
      textComponent?.shimmer();
    } catch (error) {
      console.error(error);
    } finally {
      this.#enableCartItems();
      cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
    }
  }

  /**
   * Sends a batch update to /cart/update.js (multiple line item keys at once) and refreshes
   * the relevant sections. Used whenever a main item and several add-ons need to change
   * together in a single request (bundle removal or bundle quantity sync).
   * @param {Record<string, number>} updates - Map of line item key to new quantity.
   * @param {number} [line] - The originating line item index, used to restore its input value
   *   if the server rejects the update (e.g. out of stock).
   */
  async #sendBundleUpdate(updates, line) {
    if (Object.keys(updates).length === 0) return;

    const { cartTotal } = this.refs;

    const sectionsToUpdate = new Set([this.sectionId]);
    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      updates,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    const response = await fetch('/cart/update.js', fetchConfig('json', { body }));
    const parsedResponseText = await response.json();

    resetShimmer(this);

    if (parsedResponseText.errors) {
      console.error(parsedResponseText.errors);
      if (line) this.#handleCartError(line, parsedResponseText);
      return;
    }

    const newSectionHTML = new DOMParser().parseFromString(parsedResponseText.sections[this.sectionId], 'text/html');
    const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
    const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

    this.#updateQuantitySelectors(parsedResponseText);

    this.dispatchEvent(
      new CartUpdateEvent(parsedResponseText, this.sectionId, {
        itemCount: newCartItemCount,
        source: 'cart-items-component',
        sections: parsedResponseText.sections,
      })
    );

    morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], {
      mode: this.isDrawer ? 'hydration' : 'full',
    });

    this.#updateCartQuantitySelectorButtonStates();
  }

  /**
   * Animates the given rows out, or triggers the empty-cart state if all rows are being removed.
   * @param {HTMLElement[]} rowsToRemove
   */
  #animateRowsRemoval(rowsToRemove) {
    // If the cart item rows are the last rows, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], { mode: this.isDrawer ? 'hydration' : 'full' });

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    // Looked up via the row itself (not the flat quantitySelectors[] ref array) because
    // add-on rows render no quantity-selector-component at all, which would otherwise
    // shift every index after the first add-on out of alignment with the cart line number.
    const row = this.refs.cartItemRows[line - 1];
    const quantityInput = row?.querySelector('input[name="updates[]"], input[name="quantity"]');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
